import React, {useState, useEffect} from 'react';
import axios from 'axios';
import AdminMenu from "../AdminMenu";
import AdminHeader from "../AdminHeader";
import Button from "../../../components/elements/Button";
import InputField from "../../../components/elements/InputField";
import TextArea from "../../../components/elements/TextArea";
import TagsInput from "../../../components/elements/TagsInput";
import ErrorMessage from "../../../components/layout/ErrorMessage";
import {useNavigate, useParams} from 'react-router-dom';
import {ADMIN_API_BASE} from "../../../utils/apiConfig";
import CustomSelect from "../../../components/elements/CustomSelect";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faSave} from "@fortawesome/free-solid-svg-icons";

const CourseOverview = () => {
    const [courseDetails, setCourseDetails] = useState([]);

    const [newCourseDetail, setNewCourseDetail] = useState({
        title: '',
        syllabus: [],
        description: '',
        language: ''
    });

    const [languageOptions, setLanguageOptions] = useState([]);
    const [message, setMessage] = useState({type: '', text: ''});
    const [courseId, setCourseId] = useState(useParams().id);

    useEffect(() => {
        fetchLanguages();
    }, [])

    const fetchLanguages = async () => {
        try {
            const token = await getCsrfToken();
            const res = await axios.get(`${ADMIN_API_BASE}/language`,
                {
                    headers: {
                        'csrf-token': token,
                    },
                    withCredentials: true
                }
            );

            if (res.data.success) {
                const options = res.data.languages.languages.map(l => l.id);
                setLanguageOptions(options);
            } else {
                setMessage(res.data.error);
            }
        } catch (e) {
            setMessage(e);
        }
    }

    const getCsrfToken = async () => {
        const res = await fetch(`${ADMIN_API_BASE}/csrf-token`, {
            credentials: 'include'
        });
        const data = await res.json();
        return data.csrfToken;
    };

    const fetchCourseDetails = async () => {
        try {
            const token = await getCsrfToken();

            const res = await axios.get(
                `${ADMIN_API_BASE}/course/${courseId}/details/`,
                {
                    headers: {
                        'csrf-token': token
                    },
                    withCredentials: true
                }
            );

            if (res.data.success) {
                setCourseDetails(res.data.course || []);
            }
        } catch (err) {
            console.error(err);
            setMessage({
                type: 'error',
                text: 'Failed to load course details'
            });
        }
    };

    useEffect(() => {
        if (courseId) {
            fetchCourseDetails()
        }
    }, [courseId]);

    const handleChangeTitle = (title) => {
        setNewCourseDetail((prevTitle) => ({...prevTitle, title: title}));
    }

    const handleChangeLanguage = (language) => {
        setNewCourseDetail((prev) => ({...prev, language: language}));
    }

    const handleChangeTags = (tags) => {
        setNewCourseDetail((prev) => ({...prev, syllabus: tags}));
    }

    const handleChangeDescription = (description) => {
        setNewCourseDetail((prev) => ({...prev, description: description}));
    }

    const saveNewCourseDetails = async () => {
        try {
            if (doesCourseVariationExist()) {
                setMessage({
                    type: 'error',
                    text: 'Course variation already exists'
                });
                return;
            }

            const token = await getCsrfToken();

            const res = await axios.post(
                `${ADMIN_API_BASE}/course/${courseId}/details/${newCourseDetail.language}`,
                newCourseDetail,
                {
                    headers: {
                        'csrf-token': token
                    },
                    withCredentials: true
                }
            );

            if (!res.data.success) {
                setMessage({
                    type: 'error',
                    text: res.data.course?.error || 'Failed to create'
                });
                return;
            }

            setNewCourseDetail({
                title: '',
                syllabus: [],
                description: '',
                language: ''
            });

            fetchCourseDetails();

        } catch (err) {
            console.error(err);
            setMessage({
                type: 'error',
                text: 'Failed to save course details'
            });
        }
    };

    const updateCourseDetails = async (item) => {
        console.log("Saving item:", item);

        try {
            const token = await getCsrfToken();

            const res = await axios.patch(
                `${ADMIN_API_BASE}/course/${courseId}/details/${item.language}`,
                item,
                {
                    headers: {
                        'csrf-token': token
                    },
                    withCredentials: true
                }
            );

            if (!res.data.success) {
                setMessage({
                    type: 'error',
                    text: res.data.course?.error || 'Failed to update'
                });
                return;
            }

            fetchCourseDetails();

        } catch (err) {
            console.error(err);
            setMessage({
                type: 'error',
                text: 'Failed to update course details'
            });
        }
    };

    const handleCourseDetailChange = (index, field, value) => {
        setCourseDetails(prev =>
            prev.map((item, i) =>
                i === index
                    ? {
                        ...item,
                        [field]: value
                    }
                    : item
            )
        );
    };

    const doesCourseVariationExist = () => {
        return courseDetails.some(
            detail => detail.language === newCourseDetail.language
        );
    };

    return (
        <div className="flex flex-1">
            <div className="w-1/5">
                <AdminMenu path="./"/>
            </div>
            <div className="flex-grow w-4/5">
                <AdminHeader title={`ویرایش دوره‌ی ${courseId}`}/>
                <span className='p-3 bg-red-200 flex m-10 rounded-md'>{message.text &&
                    <ErrorMessage type={message.type} message={message.text}/>}</span>
                <div className='flex flex-wrap m-10 p-2 gap-5 bg-gray-100 rounded-md'>
                    <CustomSelect className='w-1/3' title={`زبان`} options={languageOptions}
                                  value={newCourseDetail.language_id}
                                  onChange={e => {
                                      handleChangeLanguage(e.target.value)
                                  }}/>

                    <InputField title="تیتر دوره" className='w-1/3' value={newCourseDetail.title} onChange={e => {
                        handleChangeTitle(e.target.value)
                    }}/>

                    <TagsInput
                        className='w-1/3'
                        value={newCourseDetail.syllabus}
                        title="سرفصل"
                        onChange={handleChangeTags}
                    />

                    <TextArea className='w-1/2' value={newCourseDetail.description} title='توضیحات' onChange={e => {
                        handleChangeDescription(e.target.value)
                    }}/>

                    <Button onClick={saveNewCourseDetails}>
                        <span>ذخیره</span>
                        <FontAwesomeIcon icon={faSave} size={"lg"}/>
                    </Button>
                </div>
                {courseDetails.map((item, index) => (
                    <div
                        key={item.id || index}
                        className="flex flex-wrap m-10 items-center p-2 gap-5 bg-gray-100 rounded-md"
                    >
                        <CustomSelect
                            title="زبان"
                            options={languageOptions}
                            value={item.language_id}
                            onChange={(e) =>
                                handleCourseDetailChange(
                                    index,
                                    "language_id",
                                    e.target.value
                                )
                            }
                        />

                        <InputField
                            title="تیتر دوره"
                            value={item.title}
                            onChange={(e) =>
                                handleCourseDetailChange(
                                    index,
                                    "title",
                                    e.target.value
                                )
                            }
                        />

                        <TagsInput
                            title="سرفصل"
                            value={item.syllabus || []}
                            onChange={(tags) =>
                                handleCourseDetailChange(
                                    index,
                                    "syllabus",
                                    tags
                                )
                            }
                        />

                        <TextArea
                            title="توضیحات"
                            value={item.description}
                            onChange={(e) =>
                                handleCourseDetailChange(
                                    index,
                                    "description",
                                    e.target.value
                                )
                            }
                        />

                        <Button onClick={() => updateCourseDetails(item)}>
                            <span>ذخیره</span>
                            <FontAwesomeIcon icon={faSave} size="lg" />
                        </Button>
                    </div>
                ))}

            </div>
        </div>
    );
};

export default CourseOverview;
